/**
 * Coach slug registry — Firestore collection `coachSlugs`.
 *
 * Each document: coachSlugs/{slug} → { userId, createdAt }
 *
 * Document ID = slug, so:
 *   - O(1) lookup by slug (no index needed)
 *   - Uniqueness enforced at the Firestore level (a slug doc can only exist once)
 *
 * The slug is also stored on users/{userId}.coachProfile.slug for forward
 * lookup (profile page canonical URL) and for the Discover page link.
 */

import {
  doc,
  getDoc,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildSlugBase } from '../utils/slugify';

export interface CoachSlugRecord {
  userId: string;
  createdAt: unknown; // Firestore Timestamp
}

const SLUG_COLLECTION = 'coachSlugs';

/** Fetch the userId that owns a given slug, or null if unregistered. */
export async function resolveSlugToUserId(slug: string): Promise<string | null> {
  const ref = doc(db, SLUG_COLLECTION, slug);
  const snap = await getDoc(ref);
  return snap.exists() ? (snap.data() as CoachSlugRecord).userId : null;
}

/**
 * Attempt to claim `candidate` for `userId`.
 * Returns the claimed slug on success, null if the slug is taken by someone else.
 * (If the slug is already owned by the same userId it's considered already claimed.)
 */
async function tryClaimSlug(candidate: string, userId: string): Promise<string | null> {
  const ref = doc(db, SLUG_COLLECTION, candidate);
  try {
    const claimed = await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);
      if (snap.exists()) {
        const existing = snap.data() as CoachSlugRecord;
        if (existing.userId === userId) {
          // Already owned by this coach — return it as-is (no write needed)
          return candidate;
        }
        // Taken by someone else
        return null;
      }
      tx.set(ref, { userId, createdAt: serverTimestamp() });
      return candidate;
    });
    return claimed;
  } catch (err: unknown) {
    // `already-exists` (HTTP 409) is not retried by runTransaction — it fires when two
    // concurrent transactions both read the doc as non-existent and race to create it.
    // Do a plain read to see if we actually won the race (or a previous run owned it).
    const code = (err as { code?: string }).code;
    if (code === 'already-exists' || code === 'aborted') {
      try {
        const snap = await getDoc(ref);
        if (snap.exists() && (snap.data() as CoachSlugRecord).userId === userId) {
          return candidate;
        }
      } catch { /* fall through to return null */ }
    }
    return null;
  }
}

/**
 * Generate a unique slug for the coach and register it in `coachSlugs`.
 *
 * If the natural slug is taken (by a different coach), appends -2, -3, … until
 * a free slot is found (up to 20 attempts).
 *
 * Returns the claimed slug, or null if registration failed after all attempts.
 */
export async function generateAndRegisterSlug(
  userId: string,
  displayName: string,
  shortDescriptor?: string | null,
  specialties?: string[] | null,
  location?: string | null,
): Promise<string | null> {
  const base = buildSlugBase(displayName, shortDescriptor, specialties, location);
  if (!base) return null;

  // Try base slug, then base-2, base-3, …
  for (let attempt = 1; attempt <= 20; attempt++) {
    const candidate = attempt === 1 ? base : `${base}-${attempt}`;
    const claimed = await tryClaimSlug(candidate, userId);
    if (claimed !== null) return claimed;
  }

  return null;
}

/**
 * Ensure a coach has a slug registered.
 *
 * - If they already have a slug stored in Firestore (and it still points to them), return it.
 * - Otherwise generate and register a new one.
 *
 * Returns the slug string, or null on failure.
 * The caller is responsible for writing `coachProfile.slug` back to the user doc.
 */
export async function ensureCoachSlug(
  userId: string,
  currentSlug: string | undefined,
  displayName: string,
  shortDescriptor?: string | null,
  specialties?: string[] | null,
  location?: string | null,
): Promise<string | null> {
  // Verify existing slug still belongs to this coach
  if (currentSlug) {
    const owner = await resolveSlugToUserId(currentSlug);
    if (owner === userId) return currentSlug; // Already registered — nothing to do
  }

  // Generate and claim a new slug
  return generateAndRegisterSlug(userId, displayName, shortDescriptor, specialties, location);
}
